import { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Input,
  Spinner,
  toastManager,
} from "@salestrak/ui";

import type { WorkflowRow } from "#app/lib/data";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

function formatBlockerMessage(blockers: Record<string, any>[]): string {
  const issueBlocker = blockers.find((b) => b.entityType === "issues");
  if (issueBlocker) {
    return `This workflow cannot be deleted until all ${issueBlocker.count} issues are moved to a different workflow or deleted.`;
  }
  return (
    blockers
      .map((b) => (typeof b === "string" ? b : b.message ?? JSON.stringify(b)))
      .join(". ") || "This workflow cannot be deleted."
  );
}

type DeleteWorkflowDialogProps = Omittable<{
  workflow: WorkflowRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
  onManageWorkflow?: (workflowId: string) => void;
}>;

export function DeleteWorkflowDialog({
  workflow,
  open,
  onOpenChange,
  onDeleted,
  onManageWorkflow,
}: DeleteWorkflowDialogProps) {
  const { deleteWorkflow, validateWorkflowDeletion } = useWorkflowMutations();

  const [validating, setValidating] = useState(false);
  const [canDelete, setCanDelete] = useState<boolean | null>(null);
  const [blockers, setBlockers] = useState<Record<string, any>[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => {
    if (!open || !workflow) {
      setCanDelete(null);
      setBlockers([]);
      setConfirmName("");
      return;
    }

    let cancelled = false;
    setValidating(true);

    validateWorkflowDeletion(workflow.workflowId)
      .then((result) => {
        if (cancelled) return;
        setCanDelete(result.canDelete);
        setBlockers(result.blockers ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setCanDelete(false);
        setBlockers([{ message: "Failed to validate. Please try again." }]);
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, workflow, validateWorkflowDeletion]);

  const handleDelete = useCallback(async () => {
    if (!workflow) return;
    setDeleting(true);
    try {
      await deleteWorkflow(workflow.workflowId);
      toastManager.add({ title: "Workflow deleted", type: "success" });
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toastMutationError(err, "Failed to delete workflow");
    } finally {
      setDeleting(false);
    }
  }, [workflow, deleteWorkflow, onOpenChange, onDeleted]);

  const handleManage = useCallback(() => {
    if (!workflow) return;
    onOpenChange(false);
    onManageWorkflow?.(workflow.workflowId);
  }, [workflow, onOpenChange, onManageWorkflow]);

  if (!workflow) return null;

  const nameMatches = confirmName.trim() === workflow.name;
  const blockerMessage =
    canDelete === false ? formatBlockerMessage(blockers) : "";

  // One AlertDialog surface at every breakpoint.
  if (validating) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{workflow.name}&rdquo;?</AlertDialogTitle>
          </AlertDialogHeader>
          <Box padding="lg" align="center">
            <Spinner />
          </Box>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    );
  }

  if (canDelete === false) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot delete workflow</AlertDialogTitle>
            <AlertDialogDescription>{blockerMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            {onManageWorkflow ? (
              <Button onClick={handleManage}>Manage workflow</Button>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete &ldquo;{workflow.name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This workflow and its configuration will be permanently removed.
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Box paddingX="lg">
          <Input
            placeholder={`Type "${workflow.name}" to confirm`}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
        </Box>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting || !nameMatches}
            loading={deleting}
          >
            Delete workflow
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
